// src/pages/AuthCallback.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css'; // Reuse login page styles

/**
 * OAuth Callback Handler
 * Processes the authorization code from Cognito and completes authentication
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth, authConfigured } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // If auth not configured, redirect to home
    if (!authConfigured) {
      navigate('/', { replace: true });
      return;
    }

    const handleCallback = async () => {
      try {
        // Check URL for error parameters
        const params = new URLSearchParams(location.search);
        const errorParam = params.get('error');
        const errorDescription = params.get('error_description');

        if (errorParam) {
          throw new Error(errorDescription || errorParam);
        }

        // Wait for Amplify to process the OAuth code exchange
        // Poll for authenticated user with retries
        let attempts = 0;
        const maxAttempts = 10;
        let user = null;

        while (attempts < maxAttempts && !user) {
          attempts++;
          try {
            // This will succeed once Amplify has processed the code
            user = await getCurrentUser();
            console.log('User authenticated:', user.username);
          } catch (err) {
            // Not authenticated yet, wait and retry
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }

        if (!user) {
          throw new Error('Authentication timed out. Please try again.');
        }

        // Update auth context
        await checkAuth();

        // Get the original destination from session storage
        const intendedDestination = sessionStorage.getItem('auth_redirect') || '/';
        sessionStorage.removeItem('auth_redirect');

        console.log('Redirecting to:', intendedDestination);
        setProcessing(false);
        navigate(intendedDestination, { replace: true });

      } catch (err) {
        console.error('Callback error:', err);
        setError(err.message);
        setProcessing(false);

        // Redirect to error page after delay
        setTimeout(() => {
          navigate('/auth-error', {
            replace: true,
            state: { error: 'unknown_error', errorDescription: err.message }
          });
        }, 3000);
      }
    };

    handleCallback();
  }, [checkAuth, navigate, location, authConfigured]);

  if (error) {
    return (
      <div className="login-page">
        <div className="page-bg">
          <div className="bg-gradient" />
          <div className="grid-pattern" />
        </div>
        <section className="login-section">
          <div className="section-container">
            <div className="login-card">
              <div className="login-icon warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h1>Authentication Failed</h1>
              <div className="login-error">
                {error}
              </div>
              <p className="login-description">
                Redirecting to login page...
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="page-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
      </div>
      <section className="login-section">
        <div className="section-container">
          <div className="login-card">
            {processing && (
              <>
                <div className="auth-callback-spinner" />
                <h1>Completing Sign In</h1>
                <p className="login-description">
                  Please wait while we complete your authentication...
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .auth-callback-spinner {
          width: 48px;
          height: 48px;
          margin: 0 auto 1.5rem;
          border: 3px solid rgba(255, 107, 53, 0.1);
          border-top-color: var(--color-accent-primary, #ff6b35);
          border-radius: 50%;
          animation: callback-spin 1s linear infinite;
        }

        @keyframes callback-spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback;
